/**
 * TypedSignatureCapture — typed-name primitive rendered in cursive.
 *
 * Bill 2026-05-10 — Phase 1 (v0.14.17 bundle).
 *
 * Q1 doc-role tier: the default primitive for cart-embedded low-stakes
 * disclosures (Truth-in-Savings, Reg-CC, Reg-DD, Reg-E disclosures, etc.).
 * Customer types their legal name; we render it large in a cursive face
 * (Allura primary, Dancing Script secondary — both SIL OFL, bundled via
 * `@fontsource/*` so nothing fetches from a CDN at runtime).
 *
 * The rendered SVG IS the signature image — we serialize the live preview
 * to a portable SVG string at capture-time so the server can stamp it on
 * the audit page without re-running font rendering.
 */

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import type { SignatureCaptureResult } from '@retail-ops/shared-types';
// Bundle the cursive faces locally — Vite emits hashed .woff2 assets at build.
import '@fontsource/allura/400.css';
import '@fontsource/dancing-script/400.css';
// Bill 2026-07-16 — the signature-style picker (RCP-portal AdoptSignatureModal) offered
// Allura + Dancing Script, two flowing connected scripts that read identically at
// signature size. These faces widen the visual spread: a ballpoint hand, a fat brush,
// and a print-ish hand. They are imported HERE, not in the portal, because this package
// is the single font owner for every capture surface — a style declared in the picker
// without its face bundled here falls back to generic cursive and the options collapse
// back into looking the same.
import '@fontsource/homemade-apple/400.css';
import '@fontsource/satisfy/400.css';
import '@fontsource/kalam/400.css';
import '@fontsource/caveat/400.css';
import './fonts/cursive-fonts.css';

// ── Props ──────────────────────────────────────────────────────────────────

export interface TypedSignatureCaptureProps {
  signerFirstName: string;
  signerLastName: string;
  documentTitle: string;
  onCapture: (result: SignatureCaptureResult) => void;
  onCancel: () => void;
  disabled?: boolean;
  /**
   * 2026-07-02 (Bill's rule): the signature must be the signer's LEGAL name
   * exactly as it will be registered on the account — not free text. When
   * provided, Confirm stays disabled until the typed name matches
   * (whitespace-collapsed, case-insensitive).
   */
  requiredLegalName?: string;
}

// ── Styles ─────────────────────────────────────────────────────────────────

const cardS: CSSProperties = {
  background: '#fff',
  color: '#0f172a',
  borderRadius: 10,
  maxWidth: 640,
  width: '100%',
  padding: 24,
  boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  fontFamily: "'Roboto', system-ui, -apple-system, 'Segoe UI', sans-serif",
};

const headingS: CSSProperties = {
  margin: 0,
  fontSize: 18,
  fontWeight: 600,
  color: '#1a406a',
};

const subHeadS: CSSProperties = {
  margin: 0,
  fontSize: 13,
  color: '#475569',
};

const fieldRowS: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
};

const labelS: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: '#1f2937',
};

const inputS: CSSProperties = {
  padding: '10px 12px',
  border: '1px solid #cbd5e1',
  borderRadius: 6,
  fontSize: 16,
  fontFamily: 'inherit',
};

const previewWrapS: CSSProperties = {
  border: '1px dashed #cbd5e1',
  borderRadius: 8,
  padding: '24px 16px',
  background: '#f8fafc',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 110,
  overflow: 'hidden',
};

const previewS: CSSProperties = {
  fontSize: 56,
  whiteSpace: 'nowrap',
  textAlign: 'center',
};

const previewPlaceholderS: CSSProperties = {
  color: '#94a3b8',
  fontStyle: 'italic',
  fontSize: 14,
};

const checkboxRowS: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  padding: '10px 12px',
  background: '#f9fafb',
  border: '1px solid #e5e7eb',
  borderRadius: 6,
};

const checkS: CSSProperties = {
  marginTop: 2,
  width: 18,
  height: 18,
  flex: '0 0 auto',
  accentColor: '#386732',
};

const checkLabelS: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: '#1f2937',
  lineHeight: 1.4,
};

const footerS: CSSProperties = {
  display: 'flex',
  gap: 10,
  justifyContent: 'flex-end',
  marginTop: 4,
};

const primaryBtnS: CSSProperties = {
  padding: '10px 18px',
  borderRadius: 6,
  border: '1px solid #386732',
  background: '#386732',
  color: '#fff',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
};

const disabledBtnS: CSSProperties = {
  ...primaryBtnS,
  background: '#9ca3af',
  border: '1px solid #9ca3af',
  cursor: 'not-allowed',
};

const ghostBtnS: CSSProperties = {
  padding: '10px 18px',
  borderRadius: 6,
  border: '1px solid #cbd5e1',
  background: '#fff',
  color: '#1f2937',
  fontSize: 14,
  fontWeight: 500,
  cursor: 'pointer',
};

// ── Helpers ────────────────────────────────────────────────────────────────

/**
 * Build a portable SVG string the server can re-render verbatim. We embed
 * the same cursive font-family stack used in the live preview; PDF
 * generators that don't have Allura installed will fall through to
 * Snell Roundhand / Brush Script — visually similar enough that the audit
 * page is recognisable.
 *
 * Width is sized to the typed string so the SVG doesn't get clipped when
 * rasterised at smaller scales by the server.
 */
function buildSignatureSvg(text: string): string {
  const safe = text.replace(/[&<>]/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : '&gt;'
  );
  // ~16 px per char at 56 px line height — good enough for layout-side
  // estimation. The server picks up the actual `width` from the attribute.
  const estWidth = Math.max(320, safe.length * 28);
  const fontStack =
    "Allura, 'Dancing Script', 'Snell Roundhand', 'Brush Script MT', 'Brush Script Std', 'Lucida Calligraphy', 'Apple Chancery', cursive";
  return [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${estWidth}" height="90" viewBox="0 0 ${estWidth} 90">`,
    `<text x="50%" y="62" text-anchor="middle" font-family="${fontStack}" font-style="italic" font-size="56" fill="#1a406a">`,
    safe,
    `</text>`,
    `</svg>`,
  ].join('');
}

// ── Component ──────────────────────────────────────────────────────────────

export function TypedSignatureCapture(
  props: TypedSignatureCaptureProps
): JSX.Element {
  const {
    signerFirstName,
    signerLastName,
    documentTitle,
    onCapture,
    onCancel,
    disabled,
    requiredLegalName,
  } = props;

  const intentTimestampRef = useRef<string>(new Date().toISOString());

  const defaultName = useMemo(
    () => `${signerFirstName} ${signerLastName}`.trim(),
    [signerFirstName, signerLastName]
  );
  const [typedName, setTypedName] = useState<string>(defaultName);
  const [agreeSignature, setAgreeSignature] = useState<boolean>(false);
  const [agreeLegal, setAgreeLegal] = useState<boolean>(false);

  const trimmedName = typedName.trim();
  const normalize = (v: string): string => v.replace(/\s+/g, ' ').trim().toLowerCase();
  const nameMatchesLegal =
    !requiredLegalName || normalize(trimmedName) === normalize(requiredLegalName);
  const isValid =
    trimmedName.length >= 2 && nameMatchesLegal && agreeSignature && agreeLegal;

  function handleConfirm(): void {
    if (!isValid || disabled) return;
    const now = new Date().toISOString();
    const renderedSvg = buildSignatureSvg(trimmedName);
    const result: SignatureCaptureResult = {
      mode: 'typed',
      typedName: trimmedName,
      renderedSvg,
      attestationTimestamp: now,
      intentTimestamp: intentTimestampRef.current,
      signerViewportSize: `${window.innerWidth}x${window.innerHeight}`,
      signerUserAgent: navigator.userAgent,
    };
    onCapture(result);
  }

  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (
        e.key === 'Enter' &&
        // Only submit on Ctrl/Meta+Enter inside an input — bare Enter inside
        // the name field would block normal typing flow.
        (e.ctrlKey || e.metaKey) &&
        isValid &&
        !disabled
      ) {
        handleConfirm();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValid, disabled, typedName, agreeSignature, agreeLegal]);

  return (
    <div role="dialog" aria-label="Type your signature" style={cardS}>
      <h2 style={headingS}>Type your signature</h2>
      <p style={subHeadS}>
        {requiredLegalName ? (
          <>
            Type your legal name exactly as it appears on this account —{' '}
            <strong>{requiredLegalName}</strong> — to sign{' '}
            <strong>{documentTitle}</strong>. It renders in cursive and has the
            same legal effect as signing with a pen.
          </>
        ) : (
          <>
            Type your full legal name to sign <strong>{documentTitle}</strong>.
            It renders in cursive and has the same legal effect as signing with
            a pen.
          </>
        )}
      </p>

      <div style={fieldRowS}>
        <label htmlFor="ts-name" style={labelS}>
          Your full name
        </label>
        <input
          id="ts-name"
          type="text"
          autoComplete="off"
          maxLength={120}
          spellCheck={false}
          style={inputS}
          value={typedName}
          disabled={disabled}
          onChange={(e) => setTypedName(e.target.value)}
          placeholder={requiredLegalName || defaultName || 'Your full name'}
        />
        {requiredLegalName && trimmedName.length >= 2 && !nameMatchesLegal && (
          <span style={{ fontSize: 12, color: '#b10505' }} role="alert">
            The name must match exactly: {requiredLegalName}
          </span>
        )}
      </div>

      <div style={previewWrapS} aria-label="Signature preview">
        {trimmedName ? (
          <div className="signature-cursive-preview" style={previewS}>
            {trimmedName}
          </div>
        ) : (
          <div style={previewPlaceholderS}>
            Your signature preview will appear here as you type.
          </div>
        )}
      </div>

      <div style={checkboxRowS}>
        <input
          type="checkbox"
          id="ts-agree-sig"
          style={checkS}
          checked={agreeSignature}
          disabled={disabled}
          onChange={(e) => setAgreeSignature(e.target.checked)}
        />
        <label htmlFor="ts-agree-sig" style={checkLabelS}>
          I agree this is my electronic signature.
        </label>
      </div>

      <div style={checkboxRowS}>
        <input
          type="checkbox"
          id="ts-agree-legal"
          style={checkS}
          checked={agreeLegal}
          disabled={disabled}
          onChange={(e) => setAgreeLegal(e.target.checked)}
        />
        <label htmlFor="ts-agree-legal" style={checkLabelS}>
          I understand this signature has the same legal effect as a handwritten
          signature.
        </label>
      </div>

      <div style={footerS}>
        <button
          type="button"
          style={ghostBtnS}
          onClick={onCancel}
          disabled={disabled}
        >
          Cancel
        </button>
        <button
          type="button"
          style={isValid && !disabled ? primaryBtnS : disabledBtnS}
          disabled={!isValid || disabled}
          onClick={handleConfirm}
        >
          Confirm
        </button>
      </div>
    </div>
  );
}

export default TypedSignatureCapture;
